import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Eye,
  Edit,
  Send,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  Star,
  MessageSquare,
  Search,
  Download,
  TrendingUp,
  X,
  XCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText
} from "lucide-react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { toast } from "../../../utils/toast";
import { reviewApi } from "../../../api/reviewApi";
import { goalApi } from "../../../api/goalApi";
import api from "../../../api/apiClient";
import { usePerformanceCycles } from "../../../hooks/usePerformanceCycles";
import { useEmployees } from "../../../hooks/useEmployees";
import WarningPopup from "../../../components/WarningPopup";
import { formatDate } from "../../../utils/stringUtils";
import ReviewViewModal from "../../../components/common/ReviewViewModal";
import { useAuth } from "../../../context/AuthContext";
import { USER_ROLES } from "../../../utils/constants";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import useUrlTableState from "../../../hooks/useUrlTableState";

const ReviewManagement = ({ initialMode, id: propId, onClose, isDrawer }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInputRef = useRef(null);
  const { user } = useAuth();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterStatus: 'all',
    filterCycle: 'all',
    filterRating: 'all',
    filterRatingOperator: 'all',
    filterRatingValue: '',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: reviewFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateReviewFilter,
    updatePage: updateReviewPage,
    updateLimit: updateReviewLimit,
    resetFilters: resetReviewFilters,
  } = useUrlTableState(defaultFilters);

  const searchInput = searchQuery;
  const setSearchInput = setSearchQuery;
  const debouncedSearchTerm = searchQuery;

  const filterStatus = reviewFilters.filterStatus || 'all';
  const setFilterStatus = useCallback((filterStatus) => {
    updateReviewFilter({ filterStatus });
  }, [updateReviewFilter]);

  const filterCycle = reviewFilters.filterCycle || 'all';
  const setFilterCycle = useCallback((filterCycle) => {
    updateReviewFilter({ filterCycle });
  }, [updateReviewFilter]);

  const filterRating = reviewFilters.filterRating || 'all';
  const setFilterRating = useCallback((filterRating) => {
    updateReviewFilter({ filterRating });
  }, [updateReviewFilter]);

  const filterRatingOperator = reviewFilters.filterRatingOperator || 'all';
  const setFilterRatingOperator = useCallback((filterRatingOperator) => {
    updateReviewFilter({ filterRatingOperator });
  }, [updateReviewFilter]);

  const filterRatingValue = reviewFilters.filterRatingValue || '';
  const setFilterRatingValue = useCallback((filterRatingValue) => {
    updateReviewFilter({ filterRatingValue });
  }, [updateReviewFilter]);

  const filterStartDate = reviewFilters.filterStartDate || '';
  const filterEndDate = reviewFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateReviewFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateReviewFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateReviewFilter({ filterStartDate: startDate });
  }, [updateReviewFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateReviewFilter({ filterEndDate: endDate });
  }, [updateReviewFilter]);

  const page = reviewFilters.page;
  const limit = reviewFilters.limit;

  const setPage = useCallback((page) => {
    updateReviewPage(page);
  }, [updateReviewPage]);

  const setLimit = useCallback((limit) => {
    updateReviewLimit(limit);
  }, [updateReviewLimit]);
  const [activeTab, setActiveTab] = useState("self");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editReview, setEditReview] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Row selection state
  const [selectedReviews, setSelectedReviews] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const selectedExportWrapRef = useRef(null);


  const handleSelectAll = (e) => {
    const currentPageIds = filteredReviews.map(r => r.id);
    const selectedOnPage = currentPageIds.filter(id => selectedReviews.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING (as per GoalManagement pattern)
      setSelectedReviews(new Map());
    } else {
      // Otherwise select all on current page
      const newSelectedMap = new Map(selectedReviews);
      filteredReviews.forEach((review) => {
        newSelectedMap.set(review.id, review);
      });
      setSelectedReviews(newSelectedMap);
    }
  };

  const handleSelectRow = (review) => {
    setSelectedReviews((prev) => {
      const next = new Map(prev);
      if (next.has(review.id)) {
        next.delete(review.id);
      } else {
        next.set(review.id, review);
      }
      return next;
    });
  };

  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reviewToReject, setReviewToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [reviewToApprove, setReviewToApprove] = useState(null);
  const [approveReason, setApproveReason] = useState("");
  const [createErrors, setCreateErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    let error = undefined;
    switch (name) {
      case 'employee':
        if (!value) error = "Employee is required";
        break;
      case 'manager':
        if (activeTab !== 'peer' && !value) error = "Manager is required";
        break;
      case 'reviewer':
        if (activeTab === 'peer' && !value) error = "Reviewer is required";
        break;
      case 'rating':
        if (value === null || value === undefined || value === "") error = "Rating is required";
        else {
          const num = parseFloat(value);
          if (isNaN(num) || num < 0 || num > 5) error = "Rating must be between 0 and 5";
        }
        break;
      case 'goalsCompleted':
        if (activeTab === 'self') {
          if (value === "" || value === null || value === undefined) error = "Goals Completed is required";
          else if (isNaN(parseInt(value)) || parseInt(value) < 0) error = "Must be a valid number";
        }
        break;
      case 'totalGoals':
        if (activeTab === 'self') {
          if (value === "" || value === null || value === undefined) error = "Total Goals is required";
          else if (isNaN(parseInt(value)) || parseInt(value) < 0) error = "Must be a valid number";
        }
        break;
      case 'comments':
        if (value && value.length > 1000) error = "Comments must be ≤ 1000 characters";
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setCreateErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Searchable select for employee/manager inputs
  const SearchSelect = ({ label, value, onChange, options, placeholder = "Search or select", required = false, error = false, helperText, showDetails = false, disabled = false, name, onBlur }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          if (open) {
            setOpen(false);
            if (onBlur && name) {
              onBlur({ target: { name, value } });
            }
          }
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open, onBlur, name, value]);

    const normalized = Array.isArray(options)
      ? options.map((opt) => {
        if (typeof opt === 'string') {
          return { label: opt, displayLabel: opt };
        }
        const mainLabel = opt.label || opt.name || opt.fullName || "";
        const employeeId = opt.employeeId || opt.id || opt._id || "";
        const department = opt.department || "";

        // If showDetails is true, create a detailed display label
        const displayLabel = showDetails && (employeeId || department)
          ? `${mainLabel}${employeeId ? ` - ${employeeId}` : ''}${department ? ` - ${department}` : ''}`
          : mainLabel;

        return {
          label: mainLabel,
          displayLabel: displayLabel,
          employeeId: employeeId,
          department: department,
          ...opt
        };
      })
      : [];
    const filtered = normalized.filter((o) => {
      const searchText = query.toLowerCase();
      return o.label.toLowerCase().includes(searchText) ||
        (o.employeeId && o.employeeId.toLowerCase().includes(searchText)) ||
        (o.department && o.department.toLowerCase().includes(searchText));
    });
    return (
      <div className="relative" ref={dropdownRef}>
        {label ? (
          <label className="block text-sm font-medium text-gray-700">{label}</label>
        ) : null}
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          className={`mt-1 w-full border rounded-md p-2 flex items-center justify-between text-left overflow-hidden outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${error ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
        >
          <span className={`flex-1 truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
            {value || placeholder}
          </span>
          <span className="h-4 w-4 text-gray-500">▾</span>
        </button>
        {open && !disabled && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setQuery(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                placeholder={placeholder}
                className="w-full border border-gray-300 rounded-md p-2 text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No results</div>
              )}
              {filtered.map((opt, idx) => (
                <button
                  key={`${opt.label}-${idx}`}
                  type="button"
                  onClick={() => { onChange(opt.label); setOpen(false); setQuery(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-100/60 text-sm"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{opt.label}</span>
                    {showDetails && (opt.employeeId || opt.department) && (
                      <span className="text-xs text-gray-500 mt-0.5">
                        {opt.employeeId && opt.department
                          ? `${opt.employeeId} - ${opt.department}`
                          : opt.employeeId || opt.department}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {(helperText || (required && !value && error)) && (
          <p className={`mt-1 text-xs ${error ? 'text-red-600 flex items-center' : 'text-gray-500'}`}>
            {error && <AlertCircle className="h-4 w-4 mr-1" />}
            {helperText || 'Required'}
          </p>
        )}
      </div>
    );
  };

  const initialNewReview = {
    employee: "",
    manager: "",
    reviewer: "",
    cycle: "",
    submittedAt: null,
    dueDate: "",
    rating: null,
    progress: "",
    goalsCompleted: "",
    totalGoals: "",
    comments: "",
  };
  const [newReview, setNewReview] = useState(initialNewReview);

  // API Integration - Memoize filters to prevent infinite re-renders
  const filters = useMemo(
    () => {
      const filterObj = {
        reviewType: activeTab,
      };

      // Add status filter
      if (filterStatus !== "all" && filterStatus !== "") {
        filterObj.status = filterStatus;
      }

      // Always include search if it has a value (trimmed)
      // This ensures the filters object changes when debouncedSearchTerm changes
      const trimmedSearch = debouncedSearchTerm?.trim() || "";
      if (trimmedSearch !== "") {
        filterObj.search = trimmedSearch;
      }

      // Add rating filter parameters for backend
      if (filterRatingOperator !== "all" && filterRatingValue !== "") {
        filterObj.ratingOperator = filterRatingOperator;
        filterObj.ratingValue = filterRatingValue;
      }

      if (filterStartDate) filterObj.createdAtStart = filterStartDate;
      if (filterEndDate) filterObj.createdAtEnd = filterEndDate;

      filterObj.page = page;
      filterObj.limit = limit;

      return filterObj;
    },
    [debouncedSearchTerm, filterRatingOperator, filterRatingValue, filterStatus, filterStartDate, filterEndDate, activeTab, page, limit]
  );

  // Reset page when search or filters change - useUrlTableState handles this internally

  // React Query: Fetch reviews
  const {
    data: reviewsResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['reviews', filters],
    queryFn: async () => {
      const response = await reviewApi.getReviews(filters);
      return response;
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: 2,
  });

  // Get reviews data and pagination from API response
  const reviews = reviewsResponse?.data || [];
  const pagination = reviewsResponse?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  };

  // React Query: Create review mutation
  const createReviewMutation = useMutation({
    mutationFn: async (reviewData) => {
      const response = await reviewApi.createReview(reviewData);
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success("Review created successfully");
      closeModals();
    },
    onError: (error) => {
      console.error("❌ Create review error:", error);
      // Extract error message from the error object
      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create review. Please try again.";
      toast.error(errorMessage);
    },
  });

  // React Query: Update review mutation
  const updateReviewMutation = useMutation({
    mutationFn: async ({ reviewId, reviewData }) => {
      const response = await reviewApi.updateReview(reviewId, reviewData);
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success("Review updated successfully");
      closeModals();
    },
    onError: (error) => {
      toast.error("Failed to update review");
      console.error("Update review error:", error);
    },
  });

  // React Query: Delete review mutation
  const deleteReviewMutation = useMutation({
    mutationFn: async (reviewId) => {
      await reviewApi.deleteReview(reviewId);
      return reviewId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setShowDeleteWarning(false);
      setReviewToDelete(null);
    },
    onError: (error) => {
      console.error("❌ Delete review error:", error);
      toast.error("Failed to delete review");
    },
  });

  // React Query: Approve review mutation
  const approveReviewMutation = useMutation({
    mutationFn: async ({ reviewId, reason }) => {
      const response = await reviewApi.approveReview(reviewId, reason);
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success("Review approved successfully");
      setShowApproveModal(false);
      setReviewToApprove(null);
      setApproveReason("");
    },
    onError: (error) => {
      console.error("❌ Approve review error:", error);
      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve review";
      toast.error(errorMessage);
    },
  });

  // React Query: Reject review mutation
  const rejectReviewMutation = useMutation({
    mutationFn: async ({ reviewId, reason }) => {
      const response = await reviewApi.rejectReview(reviewId, reason);
      return response?.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success("Review rejected successfully");
      setShowRejectModal(false);
      setReviewToReject(null);
      setRejectReason("");
    },
    onError: (error) => {
      console.error("❌ Reject review error:", error);
      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject review";
      toast.error(errorMessage);
    },
  });

  // Fetch review KPIs from backend
  const { data: reviewKPIsResponse, isLoading: kpisLoading } = useQuery({
    queryKey: ['reviewKPIs', 'self'],
    queryFn: async () => {
      const response = await reviewApi.getReviewKPIs('self');
      // Handle different response formats
      let rawData;
      if (response?.data?.data) {
        rawData = response.data.data;
      } else if (response?.data) {
        rawData = response.data;
      } else {
        rawData = {};
      }

      const totalReviews = Number(rawData.totalReviews ?? rawData.total ?? 0);
      const completed = Number(rawData.completed ?? rawData.completedCount ?? 0);
      const inProgress = Number(rawData.inProgress ?? rawData.inProgressCount ?? 0);
      const pending = rawData.pending !== undefined
        ? Number(rawData.pending)
        : Math.max(totalReviews - completed, 0);

      return {
        totalReviews,
        completed,
        inProgress,
        pending,
      };
    },
    staleTime: 0,
    retry: 2,
  });

  // Fetch employees with goal statistics from backend
  const { data: employeesWithGoalsResponse, isLoading: employeesLoading } = useQuery({
    queryKey: ['employeesWithGoals'],
    queryFn: async () => {
      const response = await goalApi.getEmployeesWithGoals();
      // Handle different response formats
      if (response?.data?.data?.employees) {
        return response.data.data.employees;
      } else if (response?.data?.employees) {
        return response.data.employees;
      } else if (Array.isArray(response?.data)) {
        return response.data;
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
  });

  // Fetch all employees for employee dropdown using React Query
  const { data: allEmployeesResponse, isLoading: allEmployeesLoading } = useQuery({
    queryKey: ['allEmployees'],
    queryFn: async () => {
      const response = await api.get('/hr/employees/dropdown');
      // Handle different response formats
      if (response?.data?.data?.employees) {
        return response.data.data.employees;
      } else if (response?.data?.employees) {
        return response.data.employees;
      } else if (Array.isArray(response?.data?.data)) {
        return response.data.data;
      } else if (Array.isArray(response?.data)) {
        return response.data;
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
  });

  // Fetch performance cycles for cycle dropdown
  const { data: cyclesResponse, isLoading: cyclesLoading } =
    usePerformanceCycles();

  // Note: Overdue status updates are now handled by the API

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "active":
        return "bg-brand/10 text-brand";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "submitted":
        return "bg-yellow-100 text-yellow-800";
      case "under_review":
        return "bg-purple-100 text-purple-800";
      case "sent_back":
        return "bg-orange-100 text-orange-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      case "draft":
        return <Clock className="h-4 w-4" />;
      case "in_progress":
        return <Edit className="h-4 w-4" />;
      case "submitted":
        return <Send className="h-4 w-4" />;
      case "under_review":
        return <Eye className="h-4 w-4" />;
      case "sent_back":
        return <MessageSquare className="h-4 w-4" />;
      case "overdue":
        return <AlertCircle className="h-4 w-4" />;
      case "cancelled":
        return <Clock className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const handleCreateReview = () => {
    setNewReview({
      ...initialNewReview,
    });
    setCreateErrors({});
    setTouched({});
    setShowCreateValidation(false);
    setShowCreateModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    setShowCreateValidation(true);

    const errors = {};
    const fieldsToValidate = ["employee", "rating"];
    if (activeTab !== "peer") {
      fieldsToValidate.push("manager");
    } else {
      fieldsToValidate.push("reviewer");
    }
    if (activeTab === "self") {
      fieldsToValidate.push("goalsCompleted", "totalGoals");
    }
    // Optional: validate comments if present
    if (newReview.comments) {
      fieldsToValidate.push("comments");
    }

    fieldsToValidate.forEach(field => {
      const error = validateField(field, newReview[field]);
      if (error) {
        errors[field] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      // Mark all fields as touched
      const allTouched = fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {});
      setTouched(allTouched);
      toast.error("Please fix the highlighted errors");
      return;
    }

    // Validate that goalsCompleted <= totalGoals
    if (activeTab === "self") {
      const goalsCompleted = parseInt(newReview.goalsCompleted) || 0;
      const totalGoals = parseInt(newReview.totalGoals) || 0;
      if (goalsCompleted > totalGoals) {
        toast.error("Goals Completed cannot exceed Total Goals");
        return;
      }
    }

    // Validate that employee and manager are not the same person
    if (activeTab !== "peer" && newReview.employee && newReview.manager) {
      const selectedEmployee = (Array.isArray(employees) ? employees : []).find((e) => {
        const name = e.name || e.fullName ||
          `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
        return name === newReview.employee;
      });
      const employeeId = selectedEmployee?.employeeId || selectedEmployee?.id || selectedEmployee?._id || "";

      const managerList = Array.isArray(managers) ? managers : [];
      const selectedManager = (Array.isArray(managerList) ? managerList : []).find((m) => {
        const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
        const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
        const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
        return name === newReview.manager;
      });
      const managerId = selectedManager?.employeeId || selectedManager?.id || selectedManager?._id || "";

      if (employeeId && managerId && employeeId === managerId) {
        toast.error("Employee and Manager cannot be the same person");
        return;
      }
    }

    try {

      // Find selected employee to get email and employeeId (search in employees with goals)
      let employeeEmail = "";
      let employeeIdValue = "";
      if (newReview.employee) {
        const selectedEmployee = (Array.isArray(employees) ? employees : []).find((e) => {
          const name = e.name || e.fullName ||
            `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
          return name === newReview.employee;
        });
        if (selectedEmployee) {
          employeeEmail = selectedEmployee.email || selectedEmployee.contactInfo?.personalEmail || selectedEmployee.user?.email || "";
          employeeIdValue = selectedEmployee.employeeId || selectedEmployee.id || selectedEmployee._id || "";
        }
      }

      // Find selected manager to get email and employeeId
      let managerEmail = "";
      let managerIdValue = "";
      if (newReview.manager) {
        const managerList = Array.isArray(managers) ? managers : [];
        const selectedManager = (Array.isArray(managerList) ? managerList : []).find((m) => {
          const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
          const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
          const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
          return name === newReview.manager;
        });
        if (selectedManager) {
          managerEmail = selectedManager.email || selectedManager.contactInfo?.personalEmail || selectedManager.user?.email || "";
          managerIdValue = selectedManager.employeeId || selectedManager.id || selectedManager._id || "";
        }
      }

      // Exclude status, dueDate, and cycle from reviewData
      const { status, dueDate, cycle, ...reviewDataWithoutStatusAndDueDate } = newReview;

      const reviewData = {
        ...reviewDataWithoutStatusAndDueDate,
        // cycle: selectedCycle?.cycleName || newReview.cycle,
        reviewType: activeTab,
        goalsCompleted:
          newReview.goalsCompleted === ""
            ? 0
            : parseInt(newReview.goalsCompleted),
        totalGoals:
          newReview.totalGoals === "" ? 0 : parseInt(newReview.totalGoals),
        employeeEmail: employeeEmail,
        managerEmail: managerEmail,
        employeeId: employeeIdValue,
        comments: newReview.comments !== undefined && newReview.comments !== null ? newReview.comments : "",
      };

      console.log("📝 Create Review - Payload being sent:", JSON.stringify(reviewData, null, 2));
      createReviewMutation.mutate(reviewData);
    } catch (error) {
      console.error("❌ Create review validation error:", error);
      // Error handling is done in mutation's onError
    }
  };

  const handleViewReview = (reviewId) => {
    const reviewsToSearch = reviews || [];
    const review = reviewsToSearch.find((r) => r.id === reviewId || r._id === reviewId);
    if (review) {
      setSelectedReview(review);
      setShowViewModal(true);
    } else {
      // If reviews are still loading, it might not be found. 
      // In a real app we might want to fetch by ID.
      console.log("Review not found in current list", reviewId);
    }
  };

  const handleEditReview = (reviewId) => {
    const reviewsToSearch = reviews || [];
    const review = reviewsToSearch.find((r) => r.id === reviewId || r._id === reviewId);
    if (!review) return;

    // Format the due date for the input field
    const formattedReview = {
      ...review,
      dueDate: review.dueDate
        ? new Date(review.dueDate).toISOString().split("T")[0]
        : "",
      progress: review.progressPercentage || "",
      // Handle 0 values properly - check for undefined/null, not falsy
      goalsCompleted: review.goalsCompleted !== undefined && review.goalsCompleted !== null ? review.goalsCompleted : "",
      totalGoals: review.totalGoals !== undefined && review.totalGoals !== null ? review.totalGoals : "",
      comments: review.comments || "",
    };
    setEditReview(formattedReview);
    setCreateErrors({});
    setTouched({});
    setShowEditModal(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    const errors = {};
    const fieldsToValidate = ["employee", "rating"];
    if (activeTab !== "peer") {
      fieldsToValidate.push("manager");
    } else {
      fieldsToValidate.push("reviewer");
    }
    if (activeTab === "self") {
      fieldsToValidate.push("goalsCompleted", "totalGoals");
    }
    // Optional: validate comments if present
    if (editReview.comments) {
      fieldsToValidate.push("comments");
    }

    fieldsToValidate.forEach(field => {
      const error = validateField(field, editReview[field]);
      if (error) {
        errors[field] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      // Mark all fields as touched
      const allTouched = fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {});
      setTouched(allTouched);
      toast.error("Please fix the highlighted errors");
      return;
    }

    // Validate that goalsCompleted <= totalGoals
    if (activeTab === "self") {
      const goalsCompleted = parseInt(editReview.goalsCompleted) || 0;
      const totalGoals = parseInt(editReview.totalGoals) || 0;
      if (goalsCompleted > totalGoals) {
        toast.error("Goals Completed cannot exceed Total Goals");
        return;
      }
    }

    // Validate that employee and manager are not the same person
    if (activeTab !== "peer" && editReview.employee && editReview.manager) {
      const selectedEmployee = (Array.isArray(employees) ? employees : []).find((e) => {
        const name = e.name || e.fullName ||
          `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
        return name === editReview.employee;
      });
      const employeeId = selectedEmployee?.employeeId || selectedEmployee?.id || selectedEmployee?._id || "";

      const managerList = Array.isArray(managers) ? managers : [];
      const selectedManager = (Array.isArray(managerList) ? managerList : []).find((m) => {
        const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
        const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
        const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
        return name === editReview.manager;
      });
      const managerId = selectedManager?.employeeId || selectedManager?.id || selectedManager?._id || "";

      if (employeeId && managerId && employeeId === managerId) {
        toast.error("Employee and Manager cannot be the same person");
        return;
      }
    }

    try {
      // Find selected employee to get email and employeeId
      // Always look up employee when employee name is provided to ensure email and employeeId are updated
      let employeeEmail = "";
      let employeeIdValue = "";
      if (editReview.employee) {
        const selectedEmployee = (Array.isArray(employees) ? employees : []).find((e) => {
          const name = e.name || e.fullName ||
            `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
          return name === editReview.employee;
        });
        if (selectedEmployee) {
          employeeEmail = selectedEmployee.email || selectedEmployee.contactInfo?.personalEmail || selectedEmployee.user?.email || "";
          employeeIdValue = selectedEmployee.employeeId || selectedEmployee.id || selectedEmployee._id || "";
        } else {
          // Fallback to existing values if employee not found
          employeeEmail = editReview.employeeEmail || "";
          employeeIdValue = editReview.employeeId || "";
        }
      }

      // Find selected manager to get email and employeeId
      // Always look up manager when manager name is provided to ensure email and employeeId are updated
      let managerEmail = "";
      let managerIdValue = "";
      if (editReview.manager) {
        const managerList = Array.isArray(managers) ? managers : [];
        const selectedManager = (Array.isArray(managerList) ? managerList : []).find((m) => {
          const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
          const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
          const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
          return name === editReview.manager;
        });
        if (selectedManager) {
          managerEmail = selectedManager.email || selectedManager.contactInfo?.personalEmail || selectedManager.user?.email || "";
          managerIdValue = selectedManager.employeeId || selectedManager.id || selectedManager._id || "";
        } else {
          // Fallback to existing values if manager not found
          managerEmail = editReview.managerEmail || "";
          managerIdValue = editReview.managerId || "";
        }
      }

      // Calculate progress percentage from goalsCompleted and totalGoals
      const totalGoals = parseInt(editReview.totalGoals || 0);
      const goalsCompleted = parseInt(editReview.goalsCompleted || 0);
      const calculatedProgress = totalGoals > 0 ? Math.round((goalsCompleted / totalGoals) * 100) : 0;

      // Only send fields that are in the edit modal
      const reviewData = {
        employee: editReview.employee,
        rating: editReview.rating,
        employeeEmail: employeeEmail,
        employeeId: employeeIdValue,
        progressPercentage: calculatedProgress,
        goalsCompleted: editReview.goalsCompleted === "" ? 0 : parseInt(editReview.goalsCompleted),
        totalGoals: editReview.totalGoals === "" ? 0 : parseInt(editReview.totalGoals),
      };

      // Add manager or reviewer based on activeTab
      if (activeTab !== "peer") {
        reviewData.manager = editReview.manager;
        reviewData.managerEmail = managerEmail;
        reviewData.managerId = managerIdValue;
      } else {
        reviewData.reviewer = editReview.reviewer;
      }

      // Add comments (always include, even if empty)
      reviewData.comments = editReview.comments !== undefined && editReview.comments !== null ? editReview.comments : "";

      console.log("📝 Update Review - Payload being sent:", JSON.stringify(reviewData, null, 2));

      updateReviewMutation.mutate({
        reviewId: editReview.id,
        reviewData: reviewData,
      });
    } catch (error) {
      toast.error("Failed to update review");
      console.error("Update review error:", error);
    }
  };

  const handleSendReminder = (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    if (review) {
      toast.success(
        `Reminder sent to ${review.employee} for review ID ${reviewId}`
      );
    }
  };

  const handleDeleteReview = (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    setIsBulkDelete(false);
    setReviewToDelete(review);
    setShowDeleteWarning(true);
  };

  const handleBulkDelete = () => {
    if (selectedReviews.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteWarning(true);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedReviews.keys());
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteReviewMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
            console.error(`Error deleting review ${id}:`, error);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} review(s) deleted successfully`);
          setSelectedReviews(new Map());
        }
        if (failCount > 0) {
          toast.error(`${failCount} review(s) could not be deleted`);
        }
      } else {
        await deleteReviewMutation.mutateAsync(reviewToDelete.id);
        toast.success("Review deleted successfully");
      }
      setShowDeleteWarning(false);
      setReviewToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  const cancelDeleteReview = () => {
    setShowDeleteWarning(false);
    setReviewToDelete(null);
    setIsBulkDelete(false);
  };

  const handleApproveReview = (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    setReviewToApprove(review);
    setApproveReason("");
    setShowApproveModal(true);
  };

  const confirmApproveReview = () => {
    if (approveReason.length > 1000) {
      toast.error("Reason cannot exceed 1000 characters");
      return;
    }
    if (reviewToApprove) {
      approveReviewMutation.mutate({
        reviewId: reviewToApprove.id,
        reason: approveReason.trim(),
      });
    }
  };

  const cancelApproveReview = () => {
    setShowApproveModal(false);
    setReviewToApprove(null);
    setApproveReason("");
  };

  const handleRejectReview = (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    setReviewToReject(review);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const confirmRejectReview = () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    if (rejectReason.length > 1000) {
      toast.error("Reason cannot exceed 1000 characters");
      return;
    }
    if (reviewToReject) {
      rejectReviewMutation.mutate({
        reviewId: reviewToReject.id,
        reason: rejectReason.trim(),
      });
    }
  };

  const cancelRejectReview = () => {
    setShowRejectModal(false);
    setReviewToReject(null);
    setRejectReason("");
  };

  // Helper function to get user ID from user object
  const getUserId = (userObj) => {
    if (!userObj) return null;
    return userObj._id || userObj.id || userObj.userId || null;
  };

  // Helper function to check if user is super admin
  const isSuperAdmin = () => {
    return user?.role === USER_ROLES.SUPER_ADMIN;
  };

  // Helper function to check if user is the review creator
  const isReviewCreator = (review) => {
    if (!user) return false;
    const userId = getUserId(user);
    const assignedById = review.assignedBy || review.createdBy;
    if (!userId || !assignedById) return false;
    return String(userId) === String(assignedById);
  };

  // Helper function to check if user is the assigned manager
  const isAssignedManager = (review) => {
    if (!user) return false;
    const userId = getUserId(user);
    const assignedToId = review.assignedTo || review.managerId;

    // Check by user ID
    if (userId && assignedToId && String(userId) === String(assignedToId)) {
      return true;
    }

    // Check by email (fallback)
    const userEmail = user.email?.toLowerCase()?.trim();
    const managerEmail = review.managerEmail?.toLowerCase()?.trim();
    if (userEmail && managerEmail && userEmail === managerEmail) {
      return true;
    }

    return false;
  };

  // Check if current user can approve or reject a review
  const canApproveOrReject = (review) => {
    if (!user) return false;

    // Super admin can always approve/reject
    if (isSuperAdmin()) {
      return true;
    }

    // Assigned manager can approve/reject
    if (isAssignedManager(review)) {
      return true;
    }

    return false;
  };

  // Check if current user can edit a review
  const canEdit = (review) => {
    if (!user) return false;

    // Super admin can always edit
    if (isSuperAdmin()) {
      return true;
    }

    // Review creator can edit
    if (isReviewCreator(review)) {
      return true;
    }

    // Assigned manager can edit
    if (isAssignedManager(review)) {
      return true;
    }

    return false;
  };

  // Check if current user can delete a review
  const canDelete = (review) => {
    if (!user) return false;

    // Super admin can always delete
    if (isSuperAdmin()) {
      return true;
    }

    // Review creator can delete
    if (isReviewCreator(review)) {
      return true;
    }

    // Assigned manager can delete
    if (isAssignedManager(review)) {
      return true;
    }

    return false;
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // --- Backend-driven Export Logic ---

  // Base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      q: debouncedSearchTerm?.trim() || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      reviewType: activeTab,
      ratingOperator: filterRatingOperator !== 'all' ? filterRatingOperator : undefined,
      ratingValue: filterRatingOperator !== 'all' && filterRatingValue ? filterRatingValue : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
    };
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });
    return apiFilters;
  }, [debouncedSearchTerm, filterStatus, activeTab, filterRatingOperator, filterRatingValue, filterStartDate, filterEndDate]);

  // Get current page review IDs
  const currentPageReviewIds = useMemo(
    () =>
      (reviews || [])
        .map((review) => {
          const raw = review?.id || review?._id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [reviews]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageReviewIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'performance-reviews',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? limit,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `performance-reviews-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, limit, currentPageReviewIds]
  );

  // Handle export for selected reviews with format selection
  const handleReviewsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedReviews.keys()).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected reviews to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedReviews, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleReviewsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              createdAtStart: startDate,
              createdAtEnd: endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageReviewIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, requestBackendExport, limit, currentPageReviewIds]
  );

  const handleExportClick = () => {
    if (selectedReviews.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Get cycles from API first (must be defined before filteredReviews)
  const cycles =
    cyclesResponse?.data?.cycles ||
    cyclesResponse?.cycles ||
    cyclesResponse?.data ||
    cyclesResponse ||
    [];

  // Function to get cycle name by ID (must be defined before filteredReviews)
  const getCycleName = (cycleId) => {
    if (!cycleId) return "N/A";
    const cycle = cycles.find((c) => c.id === cycleId || c._id === cycleId);
    return cycle ? cycle.cycleName || cycle.name || cycleId : cycleId;
  };

  // Backend handles all filtering (search, status, cycle, rating)
  // Just use the reviews returned from backend
  let filteredReviews = reviews;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedReviews = useMemo(() => {
    if (!filteredReviews) return [];
    if (!sortField) return filteredReviews;

    return [...filteredReviews].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employee || '').toLowerCase();
          bValue = (b.employee || '').toLowerCase();
          break;
        case 'manager':
          aValue = (a.manager || '').toLowerCase();
          bValue = (b.manager || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'progress':
          aValue = a.totalGoals > 0
            ? (a.goalsCompleted / a.totalGoals)
            : (typeof a.progressPercentage === 'number' ? a.progressPercentage / 100 : 0);
          bValue = b.totalGoals > 0
            ? (b.goalsCompleted / b.totalGoals)
            : (typeof b.progressPercentage === 'number' ? b.progressPercentage / 100 : 0);
          break;
        case 'rating':
          aValue = Number(a.rating || 0);
          bValue = Number(b.rating || 0);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredReviews, sortField, sortOrder]);

  // Get ALL cycles from the cycles API (not just cycles with reviews)
  const allCycles = cycles || [];

  // Create cycle options with names from all available cycles
  const cycleOptions = allCycles.map((cycle) => ({
    id: cycle.id,
    name: cycle.cycleName || cycle.name || cycle.id,
  }));

  // Get employees with goal statistics from backend API, excluding admin users
  const employees = (employeesWithGoalsResponse || []).filter((emp) => {
    const role = (emp.role || emp.user?.role || "").toLowerCase();
    const email = (emp.email || emp.user?.email || "").toLowerCase();
    const name = (emp.name || emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}` || "").toLowerCase();

    // Exclude super_admin, admin roles, and users with 'admin' in email or name
    return role !== "super_admin" &&
      role !== "admin" &&
      !role.includes("admin") &&
      !email.includes("admin") &&
      !name.includes("admin");
  });

  // Get all employees from React Query response
  const allEmployees = Array.isArray(allEmployeesResponse) ? allEmployeesResponse : [];

  const managers = (Array.isArray(allEmployees) ? allEmployees : []).filter((emp) => {
    const role = (emp.role || emp.user?.role || "").toLowerCase();
    const position =
      emp.position ||
      emp.employmentInfo?.position ||
      "";
    const normalizedPosition = position.toLowerCase();
    const email = (emp.email || emp.user?.email || "").toLowerCase();
    const name = (emp.name || emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}` || "").toLowerCase();

    // Exclude admin users
    const isAdmin = role === "super_admin" ||
      role === "admin" ||
      role.includes("admin") ||
      email.includes("admin") ||
      name.includes("admin");

    if (isAdmin) return false;

    // Include only HR managers and executives
    return role === "hr_manager" ||
      role === "hr_executive" ||
      normalizedPosition.includes("hr manager") ||
      normalizedPosition.includes("hr executive");
  });

  const getTabStats = (tab) => {
    // Use reviews from React Query
    const allReviews = reviews || [];

    // Filter reviews by reviewType
    let tabReviews = allReviews.filter((review) => {
      // Handle different possible field names for review type
      const reviewType = review.reviewType || review.type || "self";
      return reviewType === tab;
    });

    const stats = {
      total: tabReviews.length,
      completed: tabReviews.filter((r) => r.status === "approved").length,
      inProgress: tabReviews.filter((r) => r.status === "in_progress").length,
      overdue: tabReviews.filter((r) => r.status === "overdue").length,
      pending: tabReviews.filter((r) => r.status === "draft").length,
    };

    return stats;
  };

  // Render star rating with support for decimal values
  const renderStarRating = (rating) => {
    if (rating === undefined || rating === null) return null;

    const ratingValue = parseFloat(rating);
    if (isNaN(ratingValue)) return null;

    const fullStars = Math.floor(ratingValue);
    const hasPartialStar = ratingValue % 1 !== 0;
    const partialPercentage = (ratingValue % 1) * 100;

    return (
      <div className="flex items-center space-x-0.5">
        {[...Array(5)].map((_, index) => {
          if (index < fullStars) {
            // Full star
            return (
              <Star
                key={index}
                className="h-4 w-4 text-yellow-400 fill-yellow-400"
              />
            );
          } else if (index === fullStars && hasPartialStar) {
            // Partial star - gray background with yellow overlay
            return (
              <div key={index} className="relative h-4 w-4 inline-flex items-center">
                {/* Gray background star */}
                <Star className="h-4 w-4 text-gray-300 absolute" />
                {/* Yellow partial star with overflow clip */}
                <div
                  className="absolute left-0 top-0 overflow-hidden"
                  style={{ width: `${partialPercentage}%` }}
                >
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                </div>
              </div>
            );
          } else {
            // Empty star
            return (
              <Star
                key={index}
                className="h-4 w-4 text-gray-300"
              />
            );
          }
        })}
        <span className="ml-1 text-sm text-gray-600">{ratingValue}</span>
      </div>
    );
  };

  const renderFormFields = (reviewState, setReviewState, isEdit = false) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {(
        <SearchSelect
          label={<span>Employee <span className="text-red-500">*</span></span>}
          value={reviewState.employee}
          onChange={(val) => {
            // Find the selected employee from employees with goals
            const selectedEmployee = employees.find((e) => {
              const name = e.name || e.fullName ||
                `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
              return name === val;
            });

            // Check if selected employee is the same as manager
            const selectedEmployeeId = selectedEmployee?.employeeId || selectedEmployee?.id || selectedEmployee?._id || "";
            const managerMatch = (Array.isArray(managers) ? managers : []).find((m) => {
              const managerName = m.name || m.fullName ||
                `${m.personalInfo?.firstName || m.user?.firstName || ""} ${m.personalInfo?.lastName || m.user?.lastName || ""}`.trim();
              return managerName === reviewState.manager;
            });
            const managerMatchId = managerMatch?.employeeId || managerMatch?.id || managerMatch?._id || "";

            if (selectedEmployeeId && managerMatchId && selectedEmployeeId === managerMatchId) {
              toast.error("Employee and Manager cannot be the same person");
              return;
            }

            // Auto-populate totalGoals and goalsCompleted from employee's goal statistics
            const totalGoals = selectedEmployee?.totalGoals || 0;
            const completedGoals = selectedEmployee?.completedGoals || 0;

            const updatedState = {
              ...reviewState,
              employee: val,
              totalGoals: totalGoals.toString(),
              goalsCompleted: completedGoals.toString(),
            };

            // Clear manager if it's the same person
            if (selectedEmployeeId && managerMatchId && selectedEmployeeId === managerMatchId) {
              updatedState.manager = "";
            }

            setReviewState(updatedState);
          }}
          options={Array.isArray(employees) ? employees
            .filter((e) => {
              // Filter out the selected manager from employee options
              if (!reviewState.manager) return true;
              const employeeId = e.employeeId || e.id || e._id || "";
              const managerMatch = (Array.isArray(managers) ? managers : []).find((m) => {
                const managerName = m.name || m.fullName ||
                  `${m.personalInfo?.firstName || m.user?.firstName || ""} ${m.personalInfo?.lastName || m.user?.lastName || ""}`.trim();
                return managerName === reviewState.manager;
              });
              const managerMatchId = managerMatch?.employeeId || managerMatch?.id || managerMatch?._id || "";
              return employeeId !== managerMatchId;
            })
            .map((e) => {
              const name = e.name || e.fullName ||
                `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();

              return {
                label: name,
                employeeId: e.employeeId || e.id || e._id || "",
                department: e.department || e.employmentInfo?.department || "",
                totalGoals: e.totalGoals || 0,
                completedGoals: e.completedGoals || 0,
              };
            }) : []}
          placeholder={employeesLoading ? "Loading employees..." : "Search or select employee"}
          required
          name="employee"
          onBlur={handleBlur}
          error={!!(touched.employee && createErrors.employee)}
          helperText={touched.employee && createErrors.employee}
          showDetails={true}
          showGoalStats={true}
          disabled={isEdit}
        />
      )}
      {activeTab !== "peer" ? (
        <SearchSelect
          label={<span>Manager <span className="text-red-500">*</span></span>}
          value={reviewState.manager}
          onChange={(val) => {
            // Check if selected manager is the same as employee
            const managerMatch = (Array.isArray(managers) ? managers : []).find((m) => {
              const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
              const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
              const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
              return name === val;
            });
            const managerMatchId = managerMatch?.employeeId || managerMatch?.id || managerMatch?._id || "";

            const employeeMatch = employees.find((e) => {
              const name = e.name || e.fullName ||
                `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
              return name === reviewState.employee;
            });
            const employeeMatchId = employeeMatch?.employeeId || employeeMatch?.id || employeeMatch?._id || "";

            if (managerMatchId && employeeMatchId && managerMatchId === employeeMatchId) {
              toast.error("Employee and Manager cannot be the same person");
              return;
            }

            setReviewState({ ...reviewState, manager: val });
          }}
          options={(Array.isArray(managers) ? managers : [])
            .filter((m) => {
              // Filter out the selected employee from manager options
              if (!reviewState.employee) return true;
              const managerId = m.employeeId || m.id || m._id || "";
              const employeeMatch = employees.find((e) => {
                const name = e.name || e.fullName ||
                  `${e.firstName || e.personalInfo?.firstName || e.user?.firstName || ""} ${e.lastName || e.personalInfo?.lastName || e.user?.lastName || ""}`.trim();
                return name === reviewState.employee;
              });
              const employeeMatchId = employeeMatch?.employeeId || employeeMatch?.id || employeeMatch?._id || "";
              return managerId !== employeeMatchId;
            })
            .map((m) => {
              const firstName = m.personalInfo?.firstName || m.user?.firstName || m.firstName || "";
              const lastName = m.personalInfo?.lastName || m.user?.lastName || m.lastName || "";
              const name = m.name || m.fullName || `${firstName} ${lastName}`.trim();
              return {
                label: name,
                employeeId: m.employeeId || m.id || m._id || "",
                department: m.department || m.employmentInfo?.department || ""
              };
            })}
          placeholder={allEmployeesLoading ? "Loading managers..." : "Search or select manager"}
          required
          name="manager"
          onBlur={handleBlur}
          error={!!(touched.manager && createErrors.manager)}
          helperText={touched.manager && createErrors.manager}
          showDetails={true}
        />
      ) : (
        <SearchSelect
          label={<span>Reviewer <span className="text-red-500">*</span></span>}
          value={reviewState.reviewer}
          onChange={(val) => setReviewState({ ...reviewState, reviewer: val })}
          options={Array.isArray(employees) ? employees
            .filter((e) => {
              // Exclude admin users from reviewer options
              const role = (e.role || e.user?.role || "").toLowerCase();
              const email = (e.email || e.user?.email || "").toLowerCase();
              const firstName = (e.personalInfo?.firstName || e.user?.firstName || e.firstName || "").toLowerCase();
              const lastName = (e.personalInfo?.lastName || e.user?.lastName || e.lastName || "").toLowerCase();
              const name = `${firstName} ${lastName}`.toLowerCase();

              return role !== "super_admin" &&
                role !== "admin" &&
                !role.includes("admin") &&
                !email.includes("admin") &&
                !name.includes("admin");
            })
            .map((e) => ({
              label: `${(e.personalInfo?.firstName || e.user?.firstName || e.firstName || "").trim()} ${(e.personalInfo?.lastName || e.user?.lastName || e.lastName || "").trim()}`.trim()
            })) : []}
          placeholder={employeesLoading ? "Loading employees..." : "Search or select reviewer"}
          required
          name="reviewer"
          onBlur={handleBlur}
          error={!!(touched.reviewer && createErrors.reviewer)}
          helperText={touched.reviewer && createErrors.reviewer}
        />
      )}

      {(activeTab === "self") && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Total Goals <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="totalGoals"
              min="0"
              value={reviewState.totalGoals !== undefined && reviewState.totalGoals !== null ? reviewState.totalGoals : ""}
              disabled
              readOnly
              className={`mt-1 block w-full border rounded-md shadow-sm p-2 bg-gray-50 cursor-not-allowed ${touched.totalGoals && createErrors.totalGoals ? "border-red-500" : "border-gray-300"}`}
              required
            />
            {touched.totalGoals && createErrors.totalGoals ? (
              <p className="mt-1 text-xs text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {createErrors.totalGoals}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {reviewState.totalGoals || 0} total goals (Auto-populated from employee's goal statistics)
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Goals Completed <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="goalsCompleted"
              min="0"
              max={reviewState.totalGoals !== undefined && reviewState.totalGoals !== null ? reviewState.totalGoals : undefined}
              value={reviewState.goalsCompleted !== undefined && reviewState.goalsCompleted !== null ? reviewState.goalsCompleted : ""}
              disabled
              readOnly
              className={`mt-1 block w-full border rounded-md shadow-sm p-2 bg-gray-50 cursor-not-allowed ${touched.goalsCompleted && createErrors.goalsCompleted ? "border-red-500" : "border-gray-300"}`}
              required
            />
            {touched.goalsCompleted && createErrors.goalsCompleted ? (
              <p className="mt-1 text-xs text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {createErrors.goalsCompleted}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {reviewState.goalsCompleted || 0} / {reviewState.totalGoals || 0} goals (Auto-populated from employee's goal statistics)
              </p>
            )}
          </div>
        </>
      )}

      {(activeTab === "self") && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Progress (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={(() => {
              const totalGoals = parseInt(reviewState.totalGoals || 0);
              const goalsCompleted = parseInt(reviewState.goalsCompleted || 0);
              if (totalGoals > 0) {
                return Math.round((goalsCompleted / totalGoals) * 100);
              }
              return "";
            })()}
            readOnly
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 bg-gray-50 cursor-not-allowed"
            placeholder="Auto-calculated"
          />
          <p className="text-xs text-gray-500 mt-1">
            {(() => {
              const totalGoals = parseInt(reviewState.totalGoals || 0);
              const goalsCompleted = parseInt(reviewState.goalsCompleted || 0);
              if (totalGoals > 0) {
                return `${Math.round((goalsCompleted / totalGoals) * 100)}% (${goalsCompleted}/${totalGoals} goals)`;
              }
              return "";
            })()}
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Rating (1-5) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="rating"
          min="1"
          max="5"
          step="0.1"
          value={reviewState.rating || ""}
          onChange={(e) => {
            let value = e.target.value.replace(/[^0-9.]/g, '');
            // Allow only one decimal point
            value = value.replace(/(\..*)\./g, '$1');
            // Limit to 1-5 range
            if (value && parseFloat(value) > 5) value = '5';
            if (value && parseFloat(value) < 1) value = '1';
            setReviewState({
              ...reviewState,
              rating: value === '' ? null : parseFloat(value) || null,
            });
            if (createErrors.rating) {
              setCreateErrors((prev) => ({ ...prev, rating: undefined }));
            }
          }}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            // Prevent negative sign, 'e' or 'E' (scientific notation)
            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
              e.preventDefault();
            }
          }}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.rating && createErrors.rating ? "border-red-500" : "border-gray-300"}`}
          required
        />
        {touched.rating && createErrors.rating ? (
          <p className="mt-1 text-xs text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {createErrors.rating}
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            {reviewState.rating || 0}/5 (max)
          </p>
        )}
      </div>

      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700">
          Comments (Optional)
        </label>
        <textarea
          name="comments"
          value={reviewState.comments !== undefined && reviewState.comments !== null ? reviewState.comments : ""}
          onChange={(e) => {
            const value = e.target.value.slice(0, 1000);
            setReviewState({ ...reviewState, comments: value });
            if (createErrors.comments) {
              setCreateErrors((prev) => ({ ...prev, comments: undefined }));
            }
          }}
          onBlur={handleBlur}
          onKeyPress={handleTextKeyPress}
          maxLength={1000}
          rows={4}
          className={`mt-1 block w-full border rounded-md shadow-sm p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.comments && createErrors.comments ? "border-red-500" : "border-gray-300"}`}
          placeholder="Enter any additional comments..."
        />
        <div className="flex justify-between items-center mt-1">
          {touched.comments && createErrors.comments && (
            <p className="text-xs text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {createErrors.comments}
            </p>
          )}
          <p className="text-xs text-gray-500 ml-auto">
            {(reviewState.comments || "").length}/1000 characters
          </p>
        </div>
      </div>
    </div>
  );

  if (isDrawer) {
    if (initialMode === 'create') {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmitCreate} id="create-review-form">
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-1 h-5 bg-brand rounded-full mr-2"></div>
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {renderFormFields(newReview, setNewReview)}
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-3">
            <Button variant="primary" onClick={closeModals} className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">Cancel</Button>
            <Button variant="primary" type="submit" form="create-review-form" className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">
              Create Review
            </Button>
          </div>
        </div>
      );
    }
    if (initialMode === 'edit' && editReview) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmitEdit} id="edit-review-form">
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-1 h-5 bg-brand rounded-full mr-2"></div>
                    Edit Review
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {renderFormFields(editReview, setEditReview, true)}
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end space-x-3">
            <Button variant="primary" onClick={closeModals} className="px-6">Cancel</Button>
            <Button variant="primary" type="submit" form="edit-review-form" className="bg-brand hover:bg-brand-dark text-white border-transparent px-6">
              Update Review
            </Button>
          </div>
        </div>
      );
    }
    if (initialMode === 'view' && selectedReview) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            <ReviewViewModal
              review={selectedReview}
              isOpen={true}
              onClose={closeModals}
              isDrawer={true}
            />
          </div>
        </div>
      );
    }
    return <div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Review Management
          </h1>
          <p className="text-gray-600">
            Manage employee self and manager reviews
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedReviews.size > 0 && (
            <PermissionGate permission="performance" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedReviews.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="performance" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedReviews.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedReviews.size > 0
                    ? `Export ${selectedReviews.size} selected reviews`
                    : 'Export reviews'
                }
              >
                {selectedReviews.size > 0 ? `Export Selected (${selectedReviews.size})` : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedReviews.size > 0 && showInlineSelectedFormats}
                disabled={exportLoading}
                onSelect={handleReviewsSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="performance" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateReview}
              className="bg-brand hover:bg-brand-dark text-white border-transparent hover:text-white"
            >
              Create Review
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Review Stats */}

      {(() => {
        const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
            let bgColor = "bg-[#0F2942]";
            if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
            else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
            else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
            else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
            else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

            return (
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
                    <div className="space-y-4 flex-1">
                        <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                            {title}
                        </h3>
                        <div className="flex items-baseline space-x-2">
                            <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                                {value}
                            </span>
                            {(subtitle || trend) && (
                                <div className="flex items-center space-x-2">
                                    {subtitle && (
                                        <span className="text-sm font-medium text-slate-500">
                                            {subtitle}
                                        </span>
                                    )}
                                    {trend && (
                                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                            {trend.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
            );
        };

        const totalReviews = kpisLoading ? "..." : formatIndianNumber(reviewKPIsResponse?.totalReviews || 0);
        const completed = kpisLoading ? "..." : formatIndianNumber(reviewKPIsResponse?.completed || 0);
        const pending = kpisLoading
          ? "..."
          : formatIndianNumber(
            reviewKPIsResponse?.pending ??
            reviewKPIsResponse?.inProgress ??
            Math.max(
              (reviewKPIsResponse?.totalReviews || 0) -
              (reviewKPIsResponse?.completed || 0),
              0
            )
          );

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard icon={Users} title="Total Reviews" value={totalReviews} color="bg-brand" />
            <KpiCard icon={CheckCircle} title="Completed" value={completed} color="bg-green-600" />
            <KpiCard icon={TrendingUp} title="Pending" value={pending} color="bg-yellow-500" />
          </div>
        );
      })()}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
              <span className="ml-2 text-brand font-medium">Loading reviews...</span>
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Error loading reviews
                  </h3>
                  <p className="mt-1 text-sm text-red-700">
                    {error?.message || "Failed to load reviews"}
                  </p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          {!isLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4 w-full">
                <div className="relative flex-[2] min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by Manager or Employee..."
                    value={searchInput}
                    onChange={(e) => {
                      // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                      setSearchInput(value);
                    }}
                    onKeyPress={(e) => {
                      // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                      if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={100}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    autoFocus
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput('');
                        if (searchInputRef.current) searchInputRef.current.focus();
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>

                <div className="flex-shrink-0 flex items-center space-x-2">
                  <select
                    value={filterRatingOperator}
                    onChange={(e) => {
                      setFilterRatingOperator(e.target.value);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                  >
                    <option value="all">All Ratings</option>
                    <option value="equal">Equal to</option>
                    <option value="greater">Greater than</option>
                    <option value="greaterEqual">Greater than or equal</option>
                    <option value="less">Less than</option>
                    <option value="lessEqual">Less than or equal</option>
                  </select>
                  {filterRatingOperator !== "all" && (
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.5"
                      placeholder="Value"
                      value={filterRatingValue}
                      onChange={(e) => {
                        setFilterRatingValue(e.target.value);
                      }}
                      onKeyDown={handleNumberKeyDown}
                      className="flex-1 px-3 py-2 border-y border-r border-gray-300 rounded-r-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm pr-8"
                    />
                  )}
                </div>

                <div className="flex-shrink-0">
                  <DateRangeFilter
                    startDate={filterStartDate}
                    endDate={filterEndDate}
                    onChange={setDateRange}
                  />
                </div>

                {(searchInput || filterStatus !== 'all' || filterRatingOperator !== 'all' || filterRatingValue || filterStartDate || filterEndDate) && (
                  <div className="relative flex-shrink-0">
                    <Button
                      variant="outline"
                      icon={<X className="h-4 w-4" />}
                      onClick={() => {
                        resetReviewFilters();
                        if (searchInputRef.current) searchInputRef.current.focus();
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
              {(searchInput || filterStatus !== 'all' || filterRatingOperator !== 'all' || filterRatingValue || filterStartDate || filterEndDate) && (
                <div className="text-sm text-gray-500 flex items-center">
                  {pagination.total} review
                  {pagination.total !== 1 ? "s" : ""} found
                </div>
              )}
            </div>
          )}

          {/* Reviews List */}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded cursor-pointer"
                        checked={filteredReviews.length > 0 && filteredReviews.every(r => selectedReviews.has(r.id))}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('employee')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Employee</span>
                        {sortField === 'employee' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('manager')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Manager</span>
                        {sortField === 'manager' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortField === 'status' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                      onClick={() => handleSort('progress')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Progress</span>
                        {sortField === 'progress' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                      onClick={() => handleSort('rating')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Rating</span>
                        {sortField === 'rating' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created At</span>
                        {sortField === 'createdAt' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReviews.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews found</h3>
                          {(searchInput || filterStatus !== 'all' || filterRatingOperator !== 'all' || filterRatingValue || filterStartDate || filterEndDate) && (
                            <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                          )}
                          {!searchInput && filterStatus === 'all' && filterRatingOperator === 'all' && !filterRatingValue && !filterStartDate && !filterEndDate && (
                            <p className="text-sm text-gray-500">Get started by creating your first review.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sortedReviews.map((review) => (
                      <tr key={review.id} className={`hover:bg-blue-50/30 ${selectedReviews.has(review.id) ? 'bg-brand/5' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded cursor-pointer"
                            checked={selectedReviews.has(review.id)}
                            onChange={() => handleSelectRow(review)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                          <div className="max-w-[220px]">
                            <div
                              className="text-sm font-medium text-gray-900 truncate capitalize"
                              title={review.employee}
                            >
                              {review.employee}
                            </div>
                            {review.employeeEmail && (
                              <div
                                className="text-xs text-gray-500 mt-0.5 truncate normal-case"
                                title={review.employeeEmail}
                              >
                                {review.employeeEmail}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                          <div className="max-w-[220px]">
                            <div
                              className="text-sm text-gray-900 truncate capitalize"
                              title={review.manager}
                            >
                              {review.manager}
                            </div>
                            {review.managerEmail && (
                              <div
                                className="text-xs text-gray-500 mt-0.5 truncate normal-case"
                                title={review.managerEmail}
                              >
                                {review.managerEmail}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              review.status || "pending"
                            )}`}
                          >
                            {getStatusIcon(review.status || "pending")}
                            <span className="ml-1 capitalize">
                              {review.status ? review.status.replace("_", " ") : "pending"}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {((typeof review.progressPercentage === 'number') || (review.totalGoals && review.totalGoals > 0)) ? (
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-brand h-2 rounded-full"
                                  style={{
                                    width: `${review.totalGoals > 0
                                      ? (review.goalsCompleted / review.totalGoals) * 100
                                      : (typeof review.progressPercentage === 'number' ? review.progressPercentage : 0)
                                      }%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-900">
                                {review.totalGoals > 0
                                  ? `${review.goalsCompleted}/${review.totalGoals}`
                                  : `${typeof review.progressPercentage === 'number' ? review.progressPercentage : 0}%`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {review.rating !== undefined && review.rating !== null ? (
                            renderStarRating(review.rating)
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(review.createdAt, { includeTime: true })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="performance" action="read">
                              <button
                                onClick={() => handleViewReview(review.id)}
                                className="p-2 text-brand hover:text-brand-dark hover:bg-brand/10 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {/* Approve and Reject buttons - only for super admin and assigned manager */}
                            {(review.status === "pending" || !review.status) && canApproveOrReject(review) && (
                              <>
                                <PermissionGate permission="performance" action="update">
                                  <button
                                    onClick={() => handleApproveReview(review.id)}
                                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Approve"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="performance" action="update">
                                  <button
                                    onClick={() => handleRejectReview(review.id)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Reject"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              </>
                            )}
                            {/* Edit button - for super admin, creator, and assigned manager (only if not approved/rejected) */}
                            {canEdit(review) && review.status !== "approved" && review.status !== "rejected" && (
                              <PermissionGate permission="performance" action="update">
                                <button
                                  onClick={() => handleEditReview(review.id)}
                                  className="p-2 text-brand hover:text-brand-dark hover:bg-brand/10 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {review.status === "overdue" && (
                              <PermissionGate permission="performance" action="update">
                                <button
                                  onClick={() => handleSendReminder(review.id)}
                                  className="p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded-lg transition-colors"
                                  title="Remind"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {/* Delete button - for super admin, creator, and assigned manager */}
                            {canDelete(review) && (
                              <PermissionGate permission="performance" action="delete">
                                <button
                                  onClick={() => handleDeleteReview(review.id)}
                                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!isLoading && reviews.length > 0 && (
            <PaginationControls
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              limit={pagination.limit}
              hasNextPage={pagination.page < pagination.totalPages}
              hasPrevPage={pagination.page > 1}
              onPageChange={(newPage) => setPage(newPage)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
              }}
              border={false}
            />
          )}
        </div>
      </div>

      {/* Create Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Create New Review</h2>
              <form onSubmit={handleSubmitCreate}>
                {renderFormFields(newReview, setNewReview)}
                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(false)}
                    className="bg-brand hover:bg-brand-dark text-white border-transparent"
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action="create">
                    <Button
                      variant="primary"
                      type="submit"
                      className="bg-brand hover:bg-brand-dark text-white border-transparent"
                    >
                      Create
                    </Button>
                  </PermissionGate>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* View Modal */}
      <ReviewViewModal
        review={selectedReview}
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedReview(null);
        }}
      />

      {/* Edit Modal */}
      {
        showEditModal && editReview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Edit Review</h2>
              <form onSubmit={handleSubmitEdit}>
                {renderFormFields(editReview, setEditReview, true)}
                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    variant="primary"
                    onClick={() => setShowEditModal(false)}
                    className="bg-brand hover:bg-brand-dark text-white border-transparent"
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action="update">
                    <Button
                      variant="primary"
                      type="submit"
                      className="bg-brand hover:bg-brand-dark text-white border-transparent"
                    >
                      Save
                    </Button>
                  </PermissionGate>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Delete Warning Popup */}
      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteReview}
        onConfirm={confirmDeleteReview}
        title={isBulkDelete ? "Delete Reviews" : "Delete Review"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedReviews.size} selected review(s)? This action cannot be undone.`
            : (reviewToDelete
              ? `Are you sure you want to delete the review for ${reviewToDelete.employee}? This action cannot be undone.`
              : "Are you sure you want to delete this review?")
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Approve Review Modal */}
      {
        showApproveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 bg-brand rounded-t-lg">
                <h2 className="text-lg font-semibold text-white">Approve Review</h2>
                <button
                  onClick={cancelApproveReview}
                  className="text-white hover:bg-white/10 p-1 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {reviewToApprove
                      ? `Please provide a reason for approving the review for ${reviewToApprove.employee}.`
                      : "Please provide a reason for approving this review."}
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={approveReason}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 1000);
                      setApproveReason(value);
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={3000}
                    rows={6}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    placeholder="Enter reason for approval..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {approveReason.length}/3000 characters
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="primary"
                    onClick={cancelApproveReview}
                    className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action="update">
                    <Button
                      variant="primary"
                      onClick={confirmApproveReview}
                      className="bg-brand hover:bg-brand-dark text-white border-transparent"
                      disabled={!approveReason.trim() || approveReviewMutation.isPending}
                    >
                      {approveReviewMutation.isPending ? "Approving..." : "Approve"}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Reject Review Modal */}
      {
        showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 bg-brand rounded-t-lg">
                <h2 className="text-lg font-semibold text-white">Reject Review</h2>
                <button
                  onClick={cancelRejectReview}
                  className="text-white hover:bg-white/10 p-1 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    {reviewToReject
                      ? `Please provide a reason for rejecting the review for ${reviewToReject.employee}.`
                      : "Please provide a reason for rejecting this review."}
                  </p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 1000);
                      setRejectReason(value);
                    }}
                    onKeyPress={handleTextKeyPress}
                    maxLength={3000}
                    rows={6}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    placeholder="Enter reason for rejection..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {rejectReason.length}/3000 characters
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="primary"
                    onClick={cancelRejectReview}
                    className="bg-brand hover:bg-brand-dark text-white border-transparent px-6"
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action="update">
                    <Button
                      variant="primary"
                      onClick={confirmRejectReview}
                      className="bg-red-600 hover:bg-red-700 text-white border-transparent"
                      disabled={!rejectReason.trim() || rejectReviewMutation.isPending}
                    >
                      {rejectReviewMutation.isPending ? "Rejecting..." : "Reject"}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Reviews"
        showDateRange
        scopedFormatExport={{
          onExport: handleReviewsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default ReviewManagement;
